import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const i18n = defineMessages({
	headingClearApproval: {
		id: 'approvals.action-confirmation.modal.heading.clear-approval',
		defaultMessage: 'Clear approval?',
		description:
			'Title at the top of the confirmation dialog shown before clearing the current approval. The body of the dialog explains the consequences.',
	},
	headingCancelApproval: {
		id: 'approvals.action-confirmation.modal.heading.cancel-approval',
		defaultMessage: 'Cancel approval?',
		description:
			'Title at the top of the confirmation dialog shown before cancelling an in-progress approval. The body of the dialog explains the consequences.',
	},
	headingChangeStatus: {
		id: 'approvals.action-confirmation.modal.heading.change-status',
		defaultMessage: 'Change status?',
		description:
			'Title at the top of the confirmation dialog shown before letting the user change the page content status while an approval exists.',
	},
	bodyClearApproval: {
		id: 'approvals.action-confirmation.modal.body.clear-approval',
		defaultMessage:
			'Are you sure you want to clear approval? Previous approval activity will remain available in audit logs.',
		description:
			'Body text of the confirmation dialog shown before clearing the current approval. Reassures the user that previous approval activity is retained in audit logs.',
	},
	bodyCancelApproval: {
		id: 'approvals.action-confirmation.modal.body.cancel-approval',
		defaultMessage:
			'Are you sure you want to cancel approval? Previous approval activity will remain available in audit logs.',
		description:
			'Body text of the confirmation dialog shown before cancelling an in-progress approval. Reassures the user that previous approval activity is retained in audit logs.',
	},
	bodyChangeStatus: {
		id: 'approvals.action-confirmation.modal.body.change-status',
		defaultMessage:
			'Are you sure you want to change status? Previous approval activity will remain available in audit logs.',
		description:
			'Body text of the confirmation dialog shown before letting the user change the page content status while an approval exists. Reassures the user that previous approval activity is retained in audit logs.',
	},
	confirmClearApproval: {
		id: 'approvals.action-confirmation.modal.confirm.clear-approval',
		defaultMessage: 'Clear approval',
		description:
			'Label of the primary (confirm) button in the clear-approval confirmation dialog. Clicking proceeds with clearing the approval.',
	},
	confirmCancelApproval: {
		id: 'approvals.action-confirmation.modal.confirm.cancel-approval',
		defaultMessage: 'Cancel approval',
		description:
			'Label of the primary (confirm) button in the cancel-approval confirmation dialog. Clicking proceeds with cancelling the approval.',
	},
	confirmChangeStatus: {
		id: 'approvals.action-confirmation.modal.confirm.change-status',
		defaultMessage: 'Change status',
		description:
			'Label of the primary (confirm) button in the change-status confirmation dialog. Clicking proceeds with opening the content status menu.',
	},
	cancelButton: {
		id: 'approvals.action-confirmation.modal.cancel',
		defaultMessage: 'Cancel',
		description:
			'Label of the secondary button in the approval action confirmation dialog. Clicking dismisses the dialog without taking the destructive action.',
	},
});

const styles = cssMap({
	footerRow: {
		justifyContent: 'flex-end',
		gap: token('space.150'),
		marginTop: token('space.200'),
	},
});

export type ApprovalActionConfirmationModalVariant =
	| 'clearApproval'
	| 'cancelApproval'
	| 'changeStatus';

type ApprovalActionConfirmationModalProps = {
	variant: ApprovalActionConfirmationModalVariant;
	onConfirm: () => void | Promise<void>;
	onClose: () => void;
};

const HEADING_BY_VARIANT = {
	clearApproval: i18n.headingClearApproval,
	cancelApproval: i18n.headingCancelApproval,
	changeStatus: i18n.headingChangeStatus,
} as const;

const BODY_BY_VARIANT = {
	clearApproval: i18n.bodyClearApproval,
	cancelApproval: i18n.bodyCancelApproval,
	changeStatus: i18n.bodyChangeStatus,
} as const;

const CONFIRM_BY_VARIANT = {
	clearApproval: i18n.confirmClearApproval,
	cancelApproval: i18n.confirmCancelApproval,
	changeStatus: i18n.confirmChangeStatus,
} as const;

const TEST_ID_BY_VARIANT = {
	clearApproval: 'approval-action-confirmation-modal-clear-approval',
	cancelApproval: 'approval-action-confirmation-modal-cancel-approval',
	changeStatus: 'approval-action-confirmation-modal-change-status',
} as const;

export const ApprovalActionConfirmationModal: FC<ApprovalActionConfirmationModalProps> = ({
	variant,
	onConfirm,
	onClose,
}) => {
	const { Modal } = useChoreographerAssets();

	return (
		<ModalTransition>
			<Modal onClose={onClose} testId={TEST_ID_BY_VARIANT[variant]}>
				<ModalHeader hasCloseButton>
					<ModalTitle appearance="warning">
						<FormattedMessage {...HEADING_BY_VARIANT[variant]} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<FormattedMessage {...BODY_BY_VARIANT[variant]} />
				</ModalBody>
				<ModalFooter>
					<Inline xcss={styles.footerRow}>
						<Button
							appearance="subtle"
							onClick={onClose}
							testId="approval-action-confirmation-modal-cancel-button"
						>
							<FormattedMessage {...i18n.cancelButton} />
						</Button>
						<Button
							appearance="warning"
							onClick={onConfirm}
							testId="approval-action-confirmation-modal-confirm-button"
						>
							<FormattedMessage {...CONFIRM_BY_VARIANT[variant]} />
						</Button>
					</Inline>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
